/**
 * A aba de notas do time, portada de app.js:333-373.
 *
 * O agrupamento é por arquivo porque a pergunta de quem abre esta aba é "o que
 * o time já sabe sobre este código", e o título do grupo leva direto à doc da
 * unidade. O filtro casa contra texto, autor e arquivo de uma vez só — era o
 * `data-hay` do painel atual — e o grupo que fica sem nenhum cartão visível sai
 * junto, para não sobrar cabeçalho apontando para lista vazia.
 *
 * Aqui o cartão não tem ações: responder, editar e excluir acontecem ao lado do
 * trecho comentado, no overview e na doc, que é onde a nota tem contexto.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotas } from '../api/consultas';
import type { Nota } from '../api/tipos';
import { acionavel } from '../componentes/acionavel';
import { Carregando, Falha, Vazio } from '../componentes/Estado';
import { md } from '../md/markdown';
import { ESPECIES } from '../notas/Notas';

export function NotasDoTime({ slug }: { slug: string }) {
  const consulta = useNotas(slug);
  const navegar = useNavigate();
  const [filtro, setFiltro] = useState('');

  if (consulta.isPending) return <Carregando rotulo="as notas do time" />;
  if (consulta.error) {
    return <Falha erro={consulta.error} rotulo="as notas do time" aoRepetir={consulta.refetch} />;
  }

  const notas = consulta.data?.notes || [];

  const porUnidade = new Map<string, Nota[]>();
  for (const n of notas) {
    if (!porUnidade.has(n.unit_key)) porUnidade.set(n.unit_key, []);
    porUnidade.get(n.unit_key)!.push(n);
  }

  const q = filtro.toLowerCase();
  const casa = (n: Nota, chave: string) => !q
    || `${n.body_md} ${n.author_email || ''} ${chave}`.toLowerCase().includes(q);

  const abrirDoc = (chave: string) => navegar(`/p/${slug}/doc?key=${encodeURIComponent(chave)}`);

  const cabeca = (
    <>
      <div className="crumb"><b>{slug}</b><span className="sep">/</span>notas</div>
      <div className="sec-title">Notas do time · {notas.length}</div>
      <div className="sec-sub">
        Conhecimento humano em cima da observação da IA. Vai junto no <code>eng context</code>, no{' '}
        <code>eng doc</code> e no MCP — precedência: fatos do AST &gt; notas &gt; prosa gerada.
      </div>
      <div className="side-search" style={{ padding: '0 0 12px' }}>
        <input
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          placeholder="filtrar por texto, autor ou arquivo…"
          aria-label="Filtrar notas do time"
        />
      </div>
    </>
  );

  if (!notas.length) {
    return (
      <>
        {cabeca}
        <Vazio
          titulo="O time ainda não anotou nada neste projeto"
          acao={{ rotulo: 'Abrir o overview', ao: () => navegar(`/p/${slug}/overview`) }}
          comando={'eng note <arquivo> "o que o código não mostra"'}
        >
          Esta aba junta tudo que humanos escreveram em cima da documentação gerada. Comece abrindo
          um arquivo na barra lateral e clicando no <b>+</b> que aparece ao lado de cada trecho — ou
          escreva pelo terminal, sem sair do editor.
        </Vazio>
      </>
    );
  }

  return (
    <>
      {cabeca}
      {[...porUnidade.entries()].map(([chave, lista]) => {
        const visiveis = lista.filter((n) => casa(n, chave));
        if (!visiveis.length) return null;
        return (
          <div className="note-group" key={chave} data-unit={chave}>
            <h4 data-open={chave} {...acionavel(() => abrirDoc(chave))}>
              {chave} · {lista.length}
            </h4>
            {visiveis.map((n) => <CartaoDeNota key={n.id} n={n} />)}
          </div>
        );
      })}
    </>
  );
}

function CartaoDeNota({ n }: { n: Nota }) {
  return (
    <div className="note-card" data-note={n.id}>
      <div className="note-head">
        <span className={`note-kind nk-${n.kind || 'detalhe'}`}>
          {ESPECIES[String(n.kind)] || n.kind}
        </span>
        <span className="note-meta">
          {n.author_email || '—'} · {(n.created_at || '').slice(0, 10)}
          {n.parent_id ? ' · resposta' : ''}
        </span>
      </div>
      {/* markdown do acervo, já escapado em markdown.ts */}
      <div className="note-body" dangerouslySetInnerHTML={{ __html: md(n.body_md) }} />
    </div>
  );
}
